import { useFormState } from "common/hooks";
import { ClientUserMissingCheckoutField, IClientProfileUpdate } from "common/types";
import { isValueSet } from "common/utils";

import { OnboardingSubscriptionsStepParams } from "client/onboarding/types";
import Button from "client/common/components/buttons/Button";
import TextInput from "client/common/components/inputs/TextInput";
import { useUpdateProfileMutation } from "client/accounts/hooks";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import PhoneInput from "client/common/components/inputs/PhoneInput";

import StepLayout from "../OnboardingSubscriptionsStepLayout";

const missingFieldNameMapping: Partial<Record<ClientUserMissingCheckoutField, string>> = {
  first_name: "first name",
  last_name: "last name",
  primary_contact_number: "phone number",
};

const getMissingFieldApiKey = (missingField: ClientUserMissingCheckoutField) => {
  const mapping: Record<ClientUserMissingCheckoutField, keyof IClientProfileUpdate> = {
    first_name: "first_name",
    last_name: "last_name",
    primary_contact_number: "phone_number",
  };
  return mapping[missingField];
};

const OnboardingSubscriptionsProfileMissingFieldsStep = ({
  onNext,
  isLoadingNext,
  client,
  stepper,
}: OnboardingSubscriptionsStepParams) => {
  const [values, updateField] = useFormState<Partial<IClientProfileUpdate>>({});

  const {
    mutate: updateProfile,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateProfileMutation({
    onSuccess: onNext,
  });

  const missingFields = client.primary_client_user.missing_checkout_fields;
  const primaryMissingFieldName = missingFieldNameMapping[missingFields[0]];
  const hasRequiredFields = missingFields.every((f) =>
    //   translate the missing field -> API update that fixes it
    isValueSet(values[getMissingFieldApiKey(f)]),
  );
  const canSubmit = !isUpdating && !isLoadingNext && hasRequiredFields;

  return (
    <StepLayout stepper={stepper}>
      <StepLayout.Title>
        {missingFields.length === 1
          ? `What is your ${primaryMissingFieldName}?`
          : "We need a little more info about you!"}
      </StepLayout.Title>
      {updateError && <FetchErrorAlert error={updateError} />}
      <StepLayout.Form onSubmit={() => updateProfile(values)} canSubmit={canSubmit}>
        {missingFields.includes("first_name") && (
          <TextInput
            value={values.first_name ?? ""}
            label="First Name"
            onChange={updateField("first_name")}
            errors={updateError?.errors.first_name}
          />
        )}
        {missingFields.includes("last_name") && (
          <TextInput
            value={values.last_name ?? ""}
            label="Last Name"
            onChange={updateField("last_name")}
            errors={updateError?.errors.last_name}
          />
        )}
        {missingFields.includes("primary_contact_number") && (
          <PhoneInput
            label="Phone Number"
            value={values.phone_number ?? ""}
            onChange={updateField("phone_number")}
            errors={updateError?.errors.phone_number}
          />
        )}
        <StepLayout.Footer>
          <Button type="submit" disabled={!canSubmit} isLoading={isUpdating || isLoadingNext}>
            Next
          </Button>
        </StepLayout.Footer>
      </StepLayout.Form>
    </StepLayout>
  );
};

export default OnboardingSubscriptionsProfileMissingFieldsStep;
